<div class="ctc">
  <!-- Outside the view switch: it carries the call's audio across every state. -->
  <audio #callAudio autoplay playsinline></audio>
  <div class="ctc__stripe"></div>

  <header class="ctc__head">
    <mat-icon class="ctc__head-icon" fontSet="material-icons-outlined">phone_in_talk</mat-icon>
    <span class="ctc__title">{{ title }}</span>
    <span class="ctc__kind" [class.ctc__kind--live]="view !== 'enter'">{{ view === "enter" ? "New call" : "Outgoing call" }}</span>
    <button type="button" class="ctc__close" aria-label="Close" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <!-- 1 · Enter number -->
  <form *ngIf="view === 'enter'" class="ctc__form" novalidate (ngSubmit)="start()">
    <div class="ctc__body">
      <label class="ctc__label" for="ctc-number">Phone number</label>
      <div class="ctc__phone" [class.ctc__phone--invalid]="!!validationMessage">
        <button
          type="button"
          class="ctc__country"
          [cdkMenuTriggerFor]="countryMenu"
          [cdkMenuPosition]="countryMenuPosition"
          [attr.aria-label]="'Country calling code: ' + country.name + ' +' + country.dial"
        >
          <span class="ctc__iso">{{ country.iso }}</span>
          <span class="ctc__dial">+{{ country.dial }}</span>
          <mat-icon class="ctc__chevron">expand_more</mat-icon>
        </button>
        <input
          #numberInput
          id="ctc-number"
          class="ctc__number"
          type="tel"
          inputmode="tel"
          autocomplete="tel-national"
          aria-describedby="ctc-number-help"
          [attr.aria-invalid]="!!validationMessage"
          [placeholder]="placeholder"
          [value]="nationalLabel"
          (input)="onNumberInput($event)"
          (blur)="touched = true"
        />
      </div>
      <p id="ctc-number-help" class="ctc__help" [class.ctc__help--error]="!!validationMessage" aria-live="polite">
        <mat-icon *ngIf="validationMessage" fontSet="material-icons-outlined">error</mat-icon>{{ validationMessage || numberHint }}
      </p>
      <p class="ctc__error" *ngIf="error" role="alert">{{ error }}</p>
    </div>
    <div class="ctc__actions">
      <button type="button" class="ctc__btn ctc__btn--secondary" (click)="close()">Cancel</button>
      <button type="submit" class="ctc__btn ctc__btn--call" [disabled]="!canCall">
        <mat-icon>call</mat-icon>Call
      </button>
    </div>
  </form>

  <!-- Country picker (opens under the number field) -->
  <ng-template #countryMenu>
    <div class="ctc__menu" cdkMenu aria-label="Country calling code">
      <p class="ctc__menu-head">Country</p>
      <button
        *ngFor="let c of countries"
        type="button"
        class="ctc__menu-item"
        cdkMenuItemRadio
        [cdkMenuItemChecked]="c.iso === country.iso"
        [class.ctc__menu-item--on]="c.iso === country.iso"
        (cdkMenuItemTriggered)="selectCountry(c)"
      >
        <span class="ctc__menu-iso">{{ c.iso }}</span>
        <span class="ctc__menu-name">{{ c.name }}</span>
        <span class="ctc__menu-dial">+{{ c.dial }}</span>
        <mat-icon class="ctc__menu-check">{{ c.iso === country.iso ? "check" : "" }}</mat-icon>
      </button>
    </div>
  </ng-template>

  <!-- 2 · Connecting / 3 · Ringing / 4 · On call / 5 · Call ended -->
  <ng-container *ngIf="view !== 'enter'">
    <div class="ctc__body ctc__body--call">
      <div class="ctc__avatar-wrap" [ngClass]="'ctc__avatar-wrap--' + view">
        <div class="ctc__avatar" [ngClass]="'ctc__avatar--' + view">
          <span *ngIf="initials; else noContact">{{ initials }}</span>
          <ng-template #noContact><mat-icon fontSet="material-icons-outlined">person</mat-icon></ng-template>
        </div>
      </div>
      <p class="ctc__name">{{ contactName || numberLabel }}</p>
      <p class="ctc__sub" *ngIf="subLine">{{ subLine }}</p>

      <div class="ctc__status" role="status" aria-live="polite">
        <span class="ctc__chip" [ngClass]="'ctc__chip--' + chip.tone"><i class="ctc__chip-dot"></i>{{ chip.label }}</span>
        <span class="ctc__meter" *ngIf="view === 'ringing'">{{ ringLabel }}</span>
        <span class="ctc__meter" *ngIf="view === 'connected'">{{ duration }}</span>
        <span class="ctc__meter" *ngIf="view === 'ended'">{{ duration ? "Duration " + duration : "Not connected" }}</span>
      </div>

      <button type="button" class="ctc__link" *ngIf="audioBlocked && live" (click)="enableSound()">Turn on call sound</button>
      <p class="ctc__warning" *ngIf="warning && live">{{ warning }}</p>
      <p class="ctc__error" *ngIf="error" role="alert">{{ error }}</p>

      <!-- Transcript: live while connected, kept on the ended panel -->
      <section *ngIf="showTranscript" class="ctc__transcript ctr" aria-label="Call transcript">
        <header class="ctr__head">
          <mat-icon class="ctr__icon" fontSet="material-icons-outlined">subtitles</mat-icon>
          <span class="ctr__title">{{ view === "ended" ? "Transcript" : "Live transcript" }}</span>
          <span *ngIf="view !== 'ended'" class="ctr__state" [ngClass]="'ctr__state--' + transcriptState">
            <i class="ctr__state-dot"></i>{{ transcriptStateLabel }}
          </span>
          <span class="ctr__spacer"></span>
          <ng-container *ngIf="lines.length">
            <button type="button" class="ctr__tool" (click)="copyTranscript()">
              <mat-icon fontSet="material-icons-outlined">{{ copyState === "copied" ? "check" : "content_copy" }}</mat-icon>{{ copyLabel }}
            </button>
            <button type="button" class="ctr__tool" (click)="downloadTranscript()">
              <mat-icon fontSet="material-icons-outlined">download</mat-icon>Download
            </button>
          </ng-container>
        </header>

        <div #transcriptList class="ctr__list" role="log" aria-live="polite" aria-relevant="additions text" (scroll)="onTranscriptScroll()">
          <div
            *ngFor="let line of lines; trackBy: trackByLine"
            class="ctr__line"
            [ngClass]="'ctr__line--' + line.speaker"
            [class.ctr__line--partial]="line.partial"
          >
            <div class="ctr__meta">
              <span class="ctr__who">{{ speakerOf(line) }}</span>
              <span class="ctr__at">{{ clockOf(line) }}</span>
            </div>
            <p class="ctr__text">{{ line.text }}</p>
          </div>
          <p *ngIf="!lines.length" class="ctr__empty">{{ transcriptNotice }}</p>
        </div>

        <p *ngIf="lines.length && transcriptNotice" class="ctr__notice">{{ transcriptNotice }}</p>
      </section>
    </div>

    <div class="ctc__actions" *ngIf="view !== 'ended'; else endedActions">
      <button
        type="button"
        class="ctc__btn"
        [class.ctc__btn--secondary]="!muted"
        [class.ctc__btn--active]="muted"
        [attr.aria-pressed]="muted"
        [disabled]="!audioReady"
        (click)="toggleMute()"
      >
        <mat-icon fontSet="material-icons-outlined">{{ muted ? "mic_off" : "mic" }}</mat-icon>{{ muted ? "Unmute" : "Mute" }}
      </button>
      <button type="button" class="ctc__btn ctc__btn--end" (click)="hangUp()">
        <mat-icon>call_end</mat-icon>{{ view === "connected" ? "End call" : "Cancel" }}
      </button>
    </div>
    <ng-template #endedActions>
      <div class="ctc__actions">
        <button type="button" class="ctc__btn ctc__btn--secondary" (click)="close()">Close</button>
        <button type="button" class="ctc__btn ctc__btn--call" [disabled]="starting" (click)="callAgain()">
          <mat-icon>call</mat-icon>Call again
        </button>
      </div>
    </ng-template>
  </ng-container>
</div>
